import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, UserPlus, Users, Printer, Download } from "lucide-react";
import { exportToCSV, handlePrint } from "../../utils/exportUtils";

const AGE_GROUPS = ["mini", "junior", "senior", "adult"];

const MembersPage = () => {
  const [members, setMembers] = useState([]);
  const [contestants, setContestants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isIndividualOpen, setIsIndividualOpen] = useState(false);
  const [isFamilyOpen, setIsFamilyOpen] = useState(false);
  
  // Individual form
  const [individualForm, setIndividualForm] = useState({
    contestant_id: "",
    first_name: "",
    last_name: "",
    age_group: "mini",
    phone: ""
  });

  // Family form
  const [familyForm, setFamilyForm] = useState({
    family_name: "",
    phone: "",
    members: [{ first_name: "", last_name: "", age_group: "mini" }]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [membersRes, contestantsRes] = await Promise.all([
        axios.get(`${API}/members`),
        axios.get(`${API}/contestants`)
      ]);
      setMembers(membersRes.data);
      setContestants(contestantsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleIndividualSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/members/individual`, {
        ...individualForm,
        fee: 15.0
      });
      toast.success("Individual membership created");
      fetchData();
      setIsIndividualOpen(false);
      setIndividualForm({
        contestant_id: "",
        first_name: "",
        last_name: "",
        age_group: "mini",
        phone: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create membership");
    }
  };

  const handleFamilySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/members/family`, {
        ...familyForm,
        fee: 50.0
      });
      toast.success("Family membership created");
      fetchData();
      setIsFamilyOpen(false);
      setFamilyForm({
        family_name: "",
        phone: "",
        members: [{ first_name: "", last_name: "", age_group: "mini" }]
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create membership");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this membership?")) return;
    try {
      await axios.delete(`${API}/members/${id}`);
      toast.success("Membership deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete membership");
    }
  };

  const addFamilyMember = () => {
    setFamilyForm({
      ...familyForm,
      members: [...familyForm.members, { first_name: "", last_name: "", age_group: "mini" }]
    });
  };

  const updateFamilyMember = (index, field, value) => {
    const updated = [...familyForm.members];
    updated[index][field] = value;
    setFamilyForm({ ...familyForm, members: updated });
  };

  const removeFamilyMember = (index) => {
    if (familyForm.members.length === 1) return;
    const updated = familyForm.members.filter((_, i) => i !== index);
    setFamilyForm({ ...familyForm, members: updated });
  };

  const nonMemberContestants = contestants.filter(c => !c.is_member);

  const handleExportMembers = () => {
    const exportData = [];
    members.forEach(member => {
      if (member.type === "family") {
        member.members?.forEach(m => {
          exportData.push({
            type: "Family",
            family_name: member.family_name,
            first_name: m.first_name,
            last_name: m.last_name,
            age_group: m.age_group,
            phone: member.phone || "",
            fee: member.fee.toFixed(2)
          });
        });
      } else {
        exportData.push({
          type: "Individual",
          family_name: "",
          first_name: member.first_name,
          last_name: member.last_name,
          age_group: member.age_group,
          phone: member.phone || "",
          fee: member.fee.toFixed(2)
        });
      }
    });
    
    exportToCSV(
      exportData, 
      `membership_list_${new Date().toISOString().split('T')[0]}.csv`,
      ["type", "family_name", "first_name", "last_name", "age_group", "phone", "fee"]
    );
    toast.success("Membership list exported to CSV");
  };

  const handlePrintMembers = () => {
    handlePrint("Membership List", `Total: ${members.length} memberships`);
  };

  // Calculate totals
  const totalIndividual = members.filter(m => m.type === "individual").length;
  const totalFamily = members.filter(m => m.type === "family").length;
  const totalRevenue = members.reduce((sum, m) => sum + m.fee, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print-container" data-testid="members-page">
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="font-heading font-bold text-3xl text-text-primary">Members</h1>
          <p className="text-text-secondary mt-1">{members.length} total memberships</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrintMembers} disabled={members.length === 0} data-testid="print-members-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={handleExportMembers} disabled={members.length === 0} data-testid="export-members-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block print-header">
        <h1 className="font-heading font-bold text-2xl">TTP Play Day - Membership List</h1>
        <p>Individual: {totalIndividual} | Family: {totalFamily} | Total Revenue: ${totalRevenue.toFixed(2)}</p>
      </div>

      {/* Add Membership Buttons */}
      <div className="flex gap-3 no-print">
        {/* Individual Membership Dialog */}
        <Dialog open={isIndividualOpen} onOpenChange={setIsIndividualOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-primary text-primary-foreground" data-testid="add-individual-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Individual ($15)
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Individual Membership - $15</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleIndividualSubmit} className="space-y-4" data-testid="individual-form">
              {nonMemberContestants.length > 0 && (
                <div className="space-y-2">
                  <Label>Link Existing Contestant (Optional)</Label>
                  <Select
                    value={individualForm.contestant_id}
                    onValueChange={(value) => {
                      const c = contestants.find(c => c.id === value);
                      if (c) {
                        setIndividualForm({
                          ...individualForm,
                          contestant_id: value,
                          first_name: c.first_name,
                          last_name: c.last_name,
                          age_group: c.age_group,
                          phone: c.phone || ""
                        });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select contestant" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">New Member</SelectItem>
                      {nonMemberContestants.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.first_name} {c.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input
                    value={individualForm.first_name}
                    onChange={(e) => setIndividualForm({ ...individualForm, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input
                    value={individualForm.last_name}
                    onChange={(e) => setIndividualForm({ ...individualForm, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Age Group</Label>
                <Select
                  value={individualForm.age_group}
                  onValueChange={(value) => setIndividualForm({ ...individualForm, age_group: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_GROUPS.map((ag) => (
                      <SelectItem key={ag} value={ag} className="capitalize">
                        {ag.charAt(0).toUpperCase() + ag.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={individualForm.phone}
                  onChange={(e) => setIndividualForm({ ...individualForm, phone: e.target.value })}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsIndividualOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-primary text-primary-foreground hover:bg-primary-hover" data-testid="individual-submit">
                  Create ($15)
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Family Membership Dialog */}
        <Dialog open={isFamilyOpen} onOpenChange={setIsFamilyOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary-hover" data-testid="add-family-btn">
              <Users className="h-4 w-4 mr-2" />
              Family ($50)
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Family Membership - $50</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleFamilySubmit} className="space-y-4" data-testid="family-form">
              <div className="space-y-2">
                <Label>Family Name</Label>
                <Input
                  value={familyForm.family_name}
                  onChange={(e) => setFamilyForm({ ...familyForm, family_name: e.target.value })}
                  placeholder="e.g., Smith Family"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={familyForm.phone}
                  onChange={(e) => setFamilyForm({ ...familyForm, phone: e.target.value })}
                />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Family Members</Label>
                  <Button type="button" size="sm" variant="ghost" onClick={addFamilyMember}>
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
                {familyForm.members.map((member, idx) => (
                  <div key={idx} className="flex gap-2 items-end p-3 bg-surface-muted rounded-lg">
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs">First Name</Label>
                      <Input
                        value={member.first_name}
                        onChange={(e) => updateFamilyMember(idx, "first_name", e.target.value)}
                        required
                      />
                    </div>
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs">Last Name</Label>
                      <Input
                        value={member.last_name}
                        onChange={(e) => updateFamilyMember(idx, "last_name", e.target.value)}
                        required
                      />
                    </div>
                    <div className="w-28 space-y-1">
                      <Label className="text-xs">Age</Label>
                      <Select
                        value={member.age_group}
                        onValueChange={(value) => updateFamilyMember(idx, "age_group", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {AGE_GROUPS.map((ag) => (
                            <SelectItem key={ag} value={ag} className="capitalize">
                              {ag.charAt(0).toUpperCase() + ag.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {familyForm.members.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-10 w-10 p-0 text-status-error"
                        onClick={() => removeFamilyMember(idx)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsFamilyOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-primary text-primary-foreground hover:bg-primary-hover" data-testid="family-submit">
                  Create ($50)
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-xl border border-primary/20 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead className="font-heading font-bold text-text-primary">Type</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Name</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Members</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Phone</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Fee</TableHead>
              <TableHead className="font-heading font-bold text-text-primary text-right no-print">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-text-secondary">
                  No memberships yet
                </TableCell>
              </TableRow>
            ) : (
              members.map((member) => (
                <TableRow key={member.id} className="hover:bg-surface-muted" data-testid={`member-row-${member.id}`}>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      member.type === "family" 
                        ? "bg-accent/20 text-accent" 
                        : "bg-primary/20 text-primary-foreground"
                    }`}>
                      {member.type === "family" ? "Family" : "Individual"}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium text-text-primary">
                    {member.type === "family" 
                      ? member.family_name 
                      : `${member.first_name} ${member.last_name}`}
                  </TableCell>
                  <TableCell className="text-text-secondary">
                    {member.type === "family" 
                      ? member.members?.map(m => `${m.first_name} ${m.last_name}`).join(", ")
                      : `${member.first_name} ${member.last_name} (${member.age_group})`}
                  </TableCell>
                  <TableCell className="text-text-secondary">{member.phone || "-"}</TableCell>
                  <TableCell className="font-mono font-medium text-text-primary">
                    ${member.fee.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right no-print">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(member.id)}
                      className="h-8 w-8 p-0 text-status-error hover:text-status-error"
                      data-testid={`delete-member-${member.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Print Summary */}
      <div className="hidden print:block mt-8 pt-4 border-t">
        <p className="text-sm"><strong>Summary:</strong> Individual Memberships: {totalIndividual} × $15 = ${totalIndividual * 15}.00 | Family Memberships: {totalFamily} × $50 = ${totalFamily * 50}.00 | <strong>Total: ${totalRevenue.toFixed(2)}</strong></p>
      </div>
    </div>
  );
};

export default MembersPage;
